/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { skipToken } from '@reduxjs/toolkit/query';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import PermissionButton from 'components/buttons/PermissionButton';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { Checkbox } from 'components/ui/checkbox';
import { Tooltip } from 'components/ui/tooltip';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useAddEmployeeRolesMutation, useRemoveEmployeeRolesMutation } from 'store/api/employees';
import {
    useDeleteSystemRoleMutation,
    useGetSystemRoleAssociatedUsersListQuery,
    useGetSystemRolesListQuery,
    useUpdateSystemRoleAdministrativeRoleMutation,
    useUpdateSystemRoleSystemicRoleMutation,
} from 'store/api/systemRoles';
import { EmployeeSystemRolesDto } from 'types/employees';
import { NamedEntityMinDto } from 'types/other';
import { SystemRole } from 'types/systemRoles';
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import AssociatedUsersSelector from './AssociatedUsersSelector';
import RolesAndPermissionsTableWithFilters from './RolesAndPermissionsTable';
import { NON_EDITABLE_ROLE_CODES } from './constants/rolesAndPermissionsConstants';
import AddNewSystemRoleFormModal from './modal/AddNewSystemRoleFormModal';

/** A checkbox and its label as one click target; the label greys out with a disabled box. */
const CHECKBOX_LABEL_CLASS =
    'text-body1 mr-4 inline-flex cursor-pointer items-center gap-2 has-data-disabled:cursor-not-allowed has-data-disabled:text-fg-disabled';

type RolesAndPermissionsForm = {
    systemRole: SystemRole | null;
    associatedUsers: NamedEntityMinDto[];
};

const RolesAndPermissionsPage: FC = () => {
    const { openModal, closeModal } = useModal();
    const canMarkSystemic = useHasPermissions(['UPDATE.ROLES_AND_PERMISSIONS_MARK_AS_SYSTEMIC']);
    const [isAssociatedUsersOpen, setIsAssociatedUsersOpen] = useState(false);

    const [defaultValues, setDefaultValues] = useState<RolesAndPermissionsForm>();
    const roleSelectionFormMethods = useForm<RolesAndPermissionsForm>({
        defaultValues,
        values: defaultValues,
    });
    const { control, setValue, resetField } = roleSelectionFormMethods;

    const selectedRole = useWatch({ control, name: 'systemRole' });
    const associatedUsers = useWatch({ control, name: 'associatedUsers' });
    const portalDestinationRef = useRef<HTMLDivElement>(null);
    const [currentTime, setCurrentTime] = useState<number>();
    const [originalAssociatedUsers, setOriginalAssociatedUsers] = useState<NamedEntityMinDto[]>([]);
    const [localAdministrativeRole, setLocalAdministrativeRole] = useState<boolean | null>(null);

    const { data: systemRoles, isLoading } = useGetSystemRolesListQuery();
    const [deleteSystemRole] = useDeleteSystemRoleMutation();
    const [addEmployeeRoles] = useAddEmployeeRolesMutation();
    const [removeEmployeeRoles] = useRemoveEmployeeRolesMutation();
    const [updateSystemRoleAdministrativeRole] = useUpdateSystemRoleAdministrativeRoleMutation();
    const [updateSystemRoleSystemicRole, { isLoading: isUpdatingSystemicRole }] =
        useUpdateSystemRoleSystemicRoleMutation();
    const {
        data: associatedUsersData,
        isLoading: isLoadingAssociatedUsers,
        isFetching: isFetchingAssociatedUsers,
    } = useGetSystemRoleAssociatedUsersListQuery((isAssociatedUsersOpen && selectedRole?.id) || skipToken);

    const isAssociatedUsersDisabled = selectedRole?.id == null;
    const isRoleSystemic = selectedRole?.systemicRole ?? false;
    // Systemic roles are read-only server-side (like NON_EDITABLE_ROLE_CODES), so edit
    // affordances are disabled while a role is systemic. The systemic toggle itself stays
    // enabled so the role can be unmarked.
    const isRoleEditingDisabled =
        selectedRole != null && (NON_EDITABLE_ROLE_CODES.includes(selectedRole.code) || isRoleSystemic);
    const isDeleteDisabled =
        selectedRole?.id == null ||
        NON_EDITABLE_ROLE_CODES.includes(selectedRole.code) ||
        isRoleSystemic ||
        selectedRole?.hasAttachedEmployees;

    // TODO(react-doctor:no-derived-state-effect): hydrating RHF defaultValues from a fetched
    // list is the standard "form initial values from query" pattern; once-only init keeps RHF
    // in control of subsequent state.
    useEffect(() => {
        if (isLoading || defaultValues !== undefined) {
            return;
        }
        // eslint-disable-next-line react-hooks-js/set-state-in-effect -- one-time hydration of RHF defaultValues from the fetched system-roles list (form initial values from query)
        setDefaultValues({ systemRole: systemRoles?.at(0) ?? null, associatedUsers: [] });
    }, [systemRoles, isLoading, defaultValues]);

    // TODO(react-doctor:no-derived-state-effect): selectedRole is driven by RHF useWatch and
    // lives in form state, so a simple onChange swap is not straightforward; keeping reset
    // tied to the watched value preserves correct cleanup when role changes via any mechanism.
    useEffect(() => {
        // eslint-disable-next-line react-hooks-js/set-state-in-effect -- reset local UI state when the RHF-watched selected role changes
        setIsAssociatedUsersOpen(false);
        setLocalAdministrativeRole(null);
    }, [selectedRole]);

    // Update form when associated users data changes.
    // TODO(react-doctor:no-effect-chain / no-prop-callback-in-effect): RHF setValue +
    // local mirror state from RTK query data — this is the framework-recommended way to
    // hydrate a form field from a server response.
    useEffect(() => {
        if (associatedUsersData && isAssociatedUsersOpen) {
            const mappedUsers = associatedUsersData
                .map((user) => ({ id: user.id, name: user.name }))
                .toSorted((a, b) => a.name.localeCompare(b.name));
            setValue('associatedUsers', mappedUsers);
            // eslint-disable-next-line react-hooks-js/set-state-in-effect -- mirror the server-loaded associated users into a local baseline for change diffing
            setOriginalAssociatedUsers(mappedUsers);
        }
    }, [associatedUsersData, isAssociatedUsersOpen, setValue]);

    const openAddNewSystemRoleModal = useCallback(() => {
        openModal(
            <AddNewSystemRoleFormModal
                onClose={closeModal}
                onSave={(systemRole) => {
                    setValue('systemRole', systemRole);
                    closeModal();
                }}
            />,
            closeModal,
            {
                maxWidth: '433px',
            }
        );
    }, [openModal, closeModal, setValue]);

    const openDeleteRoleModal = useCallback(() => {
        openModal(
            <ConfirmationModal
                onCancel={closeModal}
                onConfirm={() => {
                    if (selectedRole == null) {
                        closeModal();
                        return;
                    }
                    void deleteSystemRole({ id: selectedRole.id })
                        .unwrap()
                        .then(() => {
                            closeModal();
                            resetField('systemRole', { defaultValue: null });
                        })
                        .catch(() => {
                            errorAlert(t('forms.error-submitting-the-form'));
                        });
                }}
                content={<p className="text-body1">{t('roles-and-permissions.delete-confirmation-text')}</p>}
                cancelId="no"
            />
        );
    }, [closeModal, openModal, selectedRole, deleteSystemRole, resetField]);

    const toggleAssociatedUsers = useCallback(() => {
        setIsAssociatedUsersOpen(!isAssociatedUsersOpen);
    }, [isAssociatedUsersOpen]);

    // Calculate changes to determine if save button should be enabled
    const hasAssociatedUsersChanges = useMemo(() => {
        if (!originalAssociatedUsers || !associatedUsers) return false;

        const originalIds = originalAssociatedUsers.map((user) => user.id).sort();
        const currentIds = associatedUsers.map((user) => user.id).sort();

        return originalIds.length !== currentIds.length || originalIds.some((id, index) => id !== currentIds[index]);
    }, [originalAssociatedUsers, associatedUsers]);

    // Calculate if administrative role has changed
    const hasAdministrativeRoleChange = useMemo(() => {
        if (localAdministrativeRole === null || !selectedRole) return false;
        return localAdministrativeRole !== (selectedRole.administrativeRole ?? false);
    }, [localAdministrativeRole, selectedRole]);

    // Save associated users changes
    const saveAssociatedUsers = useCallback(async () => {
        if (!selectedRole || !originalAssociatedUsers || !associatedUsers) return;

        const originalIds = new Set(originalAssociatedUsers.map((user) => user.id));
        const currentIds = new Set(associatedUsers.map((user) => user.id));

        const usersToAdd = associatedUsers.filter((user) => !originalIds.has(user.id));
        const usersToRemove = originalAssociatedUsers.filter((user) => !currentIds.has(user.id));

        try {
            const addEmployeeRoleUpdates: EmployeeSystemRolesDto[] = usersToAdd.map((user) => ({
                employeeId: user.id,
                systemRoleIds: [selectedRole.id],
            }));
            const removeEmployeeRoleUpdates: EmployeeSystemRolesDto[] = usersToRemove.map((user) => ({
                employeeId: user.id,
                systemRoleIds: [selectedRole.id],
            }));

            // unwrap() so a rejected call keeps the saved baseline (and the unsaved-changes footer)
            // instead of reporting a save that never happened; the global handler toasts the error.
            if (addEmployeeRoleUpdates.length > 0) {
                await addEmployeeRoles(addEmployeeRoleUpdates).unwrap();
            }

            if (removeEmployeeRoleUpdates.length > 0) {
                await removeEmployeeRoles(removeEmployeeRoleUpdates).unwrap();
            }

            setOriginalAssociatedUsers(associatedUsers);
            setCurrentTime(Date.now());
        } catch (error) {
            console.error('Error updating associated users:', error);
        }
    }, [selectedRole, originalAssociatedUsers, associatedUsers, addEmployeeRoles, removeEmployeeRoles]);

    // Save administrative role change
    const saveAdministrativeRole = useCallback(async () => {
        if (!selectedRole || localAdministrativeRole === null) return;

        const updatedRole = await updateSystemRoleAdministrativeRole({
            id: selectedRole.id,
            administrativeRole: localAdministrativeRole,
        }).unwrap();
        setValue('systemRole', updatedRole);
        setLocalAdministrativeRole(null);
        successAlert(t('roles-and-permissions.administrative-role-updated'));
    }, [selectedRole, localAdministrativeRole, updateSystemRoleAdministrativeRole, setValue]);

    const deleteTooltipMessage = useMemo(() => {
        if (!selectedRole) {
            return;
        }
        if (NON_EDITABLE_ROLE_CODES.includes(selectedRole.code) || selectedRole.systemicRole) {
            return t('roles-and-permissions.tooltips.protected-system-role');
        }
        if (selectedRole?.hasAttachedEmployees) {
            return t('roles-and-permissions.tooltips.associated-employees');
        }
        return '';
    }, [selectedRole]);

    const handleToggleSystemicRole = useCallback(
        async (systemicRole: boolean) => {
            if (!selectedRole) return;
            try {
                const updatedRole = await updateSystemRoleSystemicRole({
                    id: selectedRole.id,
                    systemicRole,
                }).unwrap();
                setValue('systemRole', updatedRole);
                successAlert(t('roles-and-permissions.systemic-role-updated'));
            } catch {
                errorAlert(t('roles-and-permissions.systemic-role-update-error'));
            }
        },
        [selectedRole, updateSystemRoleSystemicRole, setValue]
    );

    // Display-only label: appends a "(systemic)" suffix to systemic roles in the dropdown
    // without mutating the underlying role objects' persisted values.
    const getRoleOptionLabel = useCallback(
        (role: SystemRole) =>
            role.systemicRole ? `${role.name} (${t('roles-and-permissions.systemic-role-option-suffix')})` : role.name,
        []
    );

    if (!defaultValues) {
        return <Loader />;
    }

    const deleteRoleButton = (
        <span>
            <PermissionButton
                permissions={['UPDATE.ROLES_AND_PERMISSIONS']}
                variant="contained"
                color="error"
                disabled={isDeleteDisabled}
                onClick={openDeleteRoleModal}
            >
                <FormattedMessage id="roles-and-permissions.delete-role" />
            </PermissionButton>
        </span>
    );

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <PageHeader
                        titleId="roles-and-permissions.title"
                        buttons={<div ref={portalDestinationRef} className="flex min-h-10 justify-end gap-2.5" />}
                    />
                    <p className="text-body1 text-fg-secondary">
                        <FormattedMessage id="roles-and-permissions.minimum-required-permission" />
                    </p>
                    <div className="flex flex-wrap items-center gap-2 self-start">
                        <FormProvider {...roleSelectionFormMethods}>
                            <SelectInput
                                setValue={setValue}
                                control={control}
                                name="systemRole"
                                options={systemRoles ?? []}
                                getOptionLabel={getRoleOptionLabel}
                                getOptionIdentifier="id"
                                wrapperClassName="max-w-fit"
                                gap={0}
                            />
                        </FormProvider>
                        {selectedRole && (
                            <label className={CHECKBOX_LABEL_CLASS}>
                                <Checkbox
                                    variant="secondary"
                                    checked={
                                        localAdministrativeRole !== null
                                            ? localAdministrativeRole
                                            : (selectedRole.administrativeRole ?? false)
                                    }
                                    onCheckedChange={(checked) => {
                                        setLocalAdministrativeRole(checked);
                                    }}
                                    disabled={isRoleEditingDisabled}
                                />
                                {t('roles-and-permissions.administrative-role')}
                            </label>
                        )}
                        {selectedRole && canMarkSystemic && (
                            <label className={CHECKBOX_LABEL_CLASS}>
                                <Checkbox
                                    variant="secondary"
                                    checked={isRoleSystemic}
                                    onCheckedChange={(checked) => {
                                        void handleToggleSystemicRole(checked);
                                    }}
                                    disabled={
                                        isUpdatingSystemicRole || NON_EDITABLE_ROLE_CODES.includes(selectedRole.code)
                                    }
                                />
                                {t('roles-and-permissions.systemic-role')}
                            </label>
                        )}
                        <PermissionButton
                            permissions={['READ.ROLES_AND_PERMISSIONS']}
                            variant="contained"
                            color="info"
                            disabled={isAssociatedUsersDisabled}
                            onClick={toggleAssociatedUsers}
                        >
                            <FormattedMessage id="roles-and-permissions.associated-users" />
                        </PermissionButton>
                        {deleteTooltipMessage ? (
                            <Tooltip content={deleteTooltipMessage}>{deleteRoleButton}</Tooltip>
                        ) : (
                            deleteRoleButton
                        )}
                        <PermissionButton
                            permissions={['UPDATE.ROLES_AND_PERMISSIONS']}
                            variant="contained"
                            color="secondary"
                            onClick={openAddNewSystemRoleModal}
                        >
                            <FormattedMessage id="roles-and-permissions.add-role" />
                        </PermissionButton>
                    </div>
                    {isAssociatedUsersOpen ? (
                        isLoadingAssociatedUsers || isFetchingAssociatedUsers ? (
                            <Loader />
                        ) : (
                            <div className="mt-1">
                                <AssociatedUsersSelector
                                    value={associatedUsers ?? []}
                                    onChange={(next) => setValue('associatedUsers', next)}
                                    associatedEmployees={associatedUsersData ?? []}
                                    originalUsers={originalAssociatedUsers}
                                    onSave={() => void saveAssociatedUsers()}
                                />
                            </div>
                        )
                    ) : null}
                </div>
                {selectedRole != null ? (
                    <RolesAndPermissionsTableWithFilters
                        key={`${selectedRole.id}_${currentTime}`}
                        portalRef={portalDestinationRef}
                        systemRole={selectedRole}
                        onSave={() => {
                            setCurrentTime(Date.now());
                        }}
                        associatedUsersHasChanges={isAssociatedUsersOpen && hasAssociatedUsersChanges}
                        onSaveAssociatedUsers={saveAssociatedUsers}
                        administrativeRoleHasChanges={hasAdministrativeRoleChange}
                        onSaveAdministrativeRole={saveAdministrativeRole}
                    />
                ) : null}
            </div>
        </PaddedLayout>
    );
};

export default RolesAndPermissionsPage;
